import { API_PREFIX, HttpError, type HttpClient } from "@nasam/kernel";
import { z } from "zod";

import {
  CameraRequestError,
  type ArchiveTimeline,
  type CameraRecording,
  TalkError,
  type CameraErrorCode,
  type CameraKind,
  type CameraRepository,
  type IntercomSession,
  type LiveVideoSession,
  type TalkErrorCode,
  type VehicleCamera,
  type VehicleCameras,
  type VideoFeature,
} from "../domain";

const refSchema = z.object({ id: z.string() });

// TEMPORARY look-up (see CameraRepository.getCameras): the camera device's
// model, then the model list for that model's cameras and video features.
const deviceSchema = z.object({
  data: z.object({
    id: z.string(),
    deviceType: refSchema.nullish(),
    deviceBrand: refSchema.nullish(),
    deviceModel: z.object({ id: z.string(), nameEn: z.string().nullish() }).nullish(),
  }),
});

const modelsSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      videoChannels: z.number().nullish(),
      /** RTMP, or JT1078: standard JT/T 1078, the only one that can talk. */
      videoTransport: z.string().nullish(),
      protocol: z.object({ videoFeatures: z.array(z.string()).nullish() }).nullish(),
    }),
  ),
});

const intercomSchema = z.object({
  data: z.object({
    sessionId: z.string(),
    channel: z.number(),
    talkUrl: z.string(),
    endsAt: z.string(),
  }),
});

/** An intercom refusal names its cause in the body's `code`. */
const refusalSchema = z.object({ code: z.string() });

const TALK_ERRORS: Readonly<Record<string, TalkErrorCode>> = {
  VIDEO_FEATURE_NOT_SUPPORTED: "NOT_SUPPORTED",
  VIDEO_CHANNEL_NOT_ON_DEVICE: "NOT_SUPPORTED",
  INTERCOM_NO_SPEAKER: "NOT_SUPPORTED",
  INTERCOM_BUSY: "BUSY",
  DEVICE_OFFLINE: "OFFLINE",
  VIDEO_START_FAILED: "CAMERA_UNREACHABLE",
  VIDEO_UNAVAILABLE: "UNAVAILABLE",
};

/** The talk request's HTTP failure: by the body's `code` where it has one,
 *  else by status. */
function talkErrorOf(error: unknown): TalkError {
  if (error instanceof TalkError) return error;
  if (!(error instanceof HttpError)) return new TalkError("FAILED");
  const refusal = refusalSchema.safeParse(error.data);
  const byCode = refusal.success ? TALK_ERRORS[refusal.data.code] : undefined;
  const code: TalkErrorCode =
    byCode ??
    (error.status === 403
      ? "NO_ACCESS"
      : error.status === 400
        ? "NOT_SUPPORTED"
        : error.status === 409
          ? "BUSY"
          : error.status === 502
            ? "CAMERA_UNREACHABLE"
            : error.status === 503
              ? "UNAVAILABLE"
              : "FAILED");
  return new TalkError(code, error.message);
}

const liveSchema = z.object({
  data: z.object({
    sessionId: z.string(),
    channel: z.number(),
    hlsUrl: z.string().nullish(),
    webrtcUrl: z.string().nullish(),
    endsAt: z.string(),
  }),
});

const recordingsSchema = z.object({
  data: z.array(
    z.object({
      channel: z.number(),
      start: z.string(),
      end: z.string(),
    }),
  ),
});

const archiveSchema = z.object({
  data: z.object({
    channel: z.number(),
    from: z.string(),
    to: z.string(),
    segments: z.array(
      z.object({
        id: z.string(),
        deviceId: z.string(),
        channel: z.number(),
        start: z.string(),
        end: z.string(),
        url: z.string(),
        urlExpiresAt: z.string(),
      }),
    ),
  }),
});

const coverageSchema = z.object({
  data: z.object({
    buckets: z.array(z.object({ archivedSeconds: z.number() })),
  }),
});

const VIDEO_FEATURES: readonly VideoFeature[] = ["LIVE", "RECORDINGS", "PLAYBACK", "CLIPS", "SNAPSHOTS"];

const STORY_POSITIONS: readonly CameraKind[] = ["left", "front", "right", "rear"];

/**
 * TEMPORARY: what each channel films, by camera model, as the video docs
 * describe them -- the API only numbers channels. A JIMI JC400 has the cabin
 * camera on 1 and the road camera on 2; other dashcams follow S5's order (1
 * Left, 2 Front, 3 Right, 4 Rear, still to confirm per model). Goes once the
 * backend names each channel.
 */
export function cameraKindsOf(modelName: string | null, channelCount: number): VehicleCamera[] {
  const kinds: readonly CameraKind[] = /^JC400/i.test(modelName?.trim() ?? "")
    ? ["cabin", "road"]
    : STORY_POSITIONS;
  return Array.from({ length: Math.max(0, channelCount) }, (_, index) => ({
    channel: index + 1,
    kind: kinds[index] ?? null,
  }));
}

/** The camera request's HTTP failure, as the screen tells it. */
function cameraErrorOf(error: unknown): CameraRequestError {
  if (error instanceof CameraRequestError) return error;
  const status = error instanceof HttpError ? error.status : undefined;
  const code: CameraErrorCode =
    status === 403
      ? "NO_ACCESS"
      : status === 404
        ? "NOT_FOUND"
        : status === 409
          ? "BUSY"
          : status === 502
            ? "CAMERA_UNREACHABLE"
            : status === 503
              ? "MEDIA_UNAVAILABLE"
              : "FAILED";
  return new CameraRequestError(code, error instanceof Error ? error.message : "");
}

async function asCameraRequest<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    throw cameraErrorOf(error);
  }
}

/** Recorded video's requests: there, `409` is the device not being
 *  connected (live video's is a busy channel). */
async function asRecordedVideoRequest<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (error instanceof HttpError && error.status === 409) {
      throw new CameraRequestError("OFFLINE", error.message);
    }
    throw cameraErrorOf(error);
  }
}

/** The dashcam video API (`/devices/:deviceId/video/...`). */
export function createCameraHttpRepository(http: HttpClient): CameraRepository {
  const devicesPath = `${API_PREFIX}/devices`;
  const archivePath = (assetId: string) =>
    `${API_PREFIX}/tracking/vehicles/${encodeURIComponent(assetId)}/video/archive`;
  return {
    getCameras(deviceId) {
      return asCameraRequest(async (): Promise<VehicleCameras> => {
        const { data: device } = deviceSchema.parse(
          await http.get(`${devicesPath}/${deviceId}`),
        );
        const none: VehicleCameras = {
          deviceId: device.id,
          cameras: [],
          features: [],
          canTalk: false,
        };
        const typeId = device.deviceType?.id;
        const brandId = device.deviceBrand?.id;
        const modelId = device.deviceModel?.id;
        if (typeId === undefined || brandId === undefined || modelId === undefined) return none;
        const models = modelsSchema.parse(
          await http.get(`${devicesPath}/models`, {
            params: { deviceTypeId: typeId, deviceBrandId: brandId, limit: 100 },
          }),
        );
        const model = models.data.find((item) => item.id === modelId);
        if (model === undefined) return none;
        const features = (model.protocol?.videoFeatures ?? []).filter(
          (feature): feature is VideoFeature => VIDEO_FEATURES.includes(feature as VideoFeature),
        );
        return {
          deviceId: device.id,
          // Live is the only thing the viewer does: no LIVE, no cameras to show.
          cameras: features.includes("LIVE")
            ? cameraKindsOf(device.deviceModel?.nameEn ?? null, model.videoChannels ?? 0)
            : [],
          features,
          canTalk: model.videoTransport === "JT1078",
        };
      });
    },
    startLive(deviceId, channel) {
      return asCameraRequest(async (): Promise<LiveVideoSession> => {
        const { data } = liveSchema.parse(
          await http.post(`${devicesPath}/${deviceId}/video/live`, { channel }),
        );
        return {
          sessionId: data.sessionId,
          channel: data.channel,
          // As returned: some models stream on a longer path than the session id.
          hlsUrl: data.hlsUrl ?? null,
          webrtcUrl: data.webrtcUrl ?? null,
          endsAt: data.endsAt,
        };
      });
    },
    async stopLive(deviceId, sessionId) {
      // Sent as the viewer closes, possibly with the page: let it outlive it.
      await asCameraRequest(() =>
        http.delete(`${devicesPath}/${deviceId}/video/live/${sessionId}`, { keepalive: true }),
      );
    },
    listRecordings(deviceId, range) {
      return asRecordedVideoRequest(async (): Promise<CameraRecording[]> => {
        const { data } = recordingsSchema.parse(
          await http.get(`${devicesPath}/${deviceId}/video/recordings`, {
            // Channel 0: every channel at once.
            params: { from: range.from, to: range.to, channel: 0 },
          }),
        );
        return data.map((item) => ({
          channel: item.channel,
          startAt: item.start,
          endAt: item.end,
        }));
      });
    },
    startRecordedVideo(deviceId, channel, range) {
      return asRecordedVideoRequest(async (): Promise<LiveVideoSession> => {
        const { data } = liveSchema.parse(
          await http.post(`${devicesPath}/${deviceId}/video/playback`, {
            from: range.from,
            to: range.to,
            channel,
          }),
        );
        return {
          sessionId: data.sessionId,
          channel: data.channel,
          hlsUrl: data.hlsUrl ?? null,
          webrtcUrl: data.webrtcUrl ?? null,
          endsAt: data.endsAt,
        };
      });
    },
    async controlRecordedVideo(deviceId, sessionId, control) {
      await asRecordedVideoRequest(() =>
        http.patch(`${devicesPath}/${deviceId}/video/playback/${sessionId}`, control),
      );
    },
    async stopRecordedVideo(deviceId, sessionId) {
      try {
        // Sent as the panel closes, possibly with the page: let it outlive it.
        await http.delete(`${devicesPath}/${deviceId}/video/playback/${sessionId}`, {
          keepalive: true,
        });
      } catch (error) {
        // Already over (nobody watched, or another playback took the channel).
        if (error instanceof HttpError && error.status === 404) return;
        throw cameraErrorOf(error);
      }
    },
    getArchive(assetId, channel, range) {
      return asCameraRequest(async (): Promise<ArchiveTimeline> => {
        const { data } = archiveSchema.parse(
          await http.get(archivePath(assetId), {
            params: { channel, from: range.from, to: range.to },
          }),
        );
        return {
          channel: data.channel,
          fromMs: Date.parse(data.from),
          toMs: Date.parse(data.to),
          segments: data.segments.flatMap((segment) => {
            const startMs = Date.parse(segment.start);
            const endMs = Date.parse(segment.end);
            if (!(endMs > startMs)) return [];
            return [
              {
                id: segment.id,
                deviceId: segment.deviceId,
                channel: segment.channel,
                startMs,
                endMs,
                url: segment.url,
                urlExpiresMs: Date.parse(segment.urlExpiresAt),
              },
            ];
          }),
        };
      });
    },
    getArchivedSeconds(assetId, channel, range) {
      return asCameraRequest(async () => {
        const { data } = coverageSchema.parse(
          await http.get(`${archivePath(assetId)}/coverage`, {
            params: { channel, from: range.from, to: range.to },
          }),
        );
        return data.buckets.reduce((total, bucket) => total + bucket.archivedSeconds, 0);
      });
    },
    async startIntercom(deviceId, channel): Promise<IntercomSession> {
      try {
        const { data } = intercomSchema.parse(
          await http.post(`${devicesPath}/${deviceId}/video/intercom`, { channel }),
        );
        return data;
      } catch (error) {
        throw talkErrorOf(error);
      }
    },
    async stopIntercom(deviceId, sessionId) {
      try {
        // Hanging up as the page goes away too: let it outlive the page.
        await http.delete(`${devicesPath}/${deviceId}/video/intercom/${sessionId}`, {
          keepalive: true,
        });
      } catch (error) {
        // Already over (ended by its socket closing, or by time): ended.
        if (error instanceof HttpError && error.status === 404) return;
        throw talkErrorOf(error);
      }
    },
  };
}
